// Frontend-Spiegel der Vendor-Feature-Flags (server/config/features.js).
// Keys MÜSSEN identisch bleiben. Reihenfolge = Anzeigereihenfolge im
// Vendor-Tab der Einstellungen.
//
// Abgrenzung zu capabilities.ts: Capabilities steuern, WER in der Praxis einen
// Bereich nutzen darf (pro Mitarbeiter); Features steuern, OB die Praxis den
// Bereich gebucht hat (Paket-Modell, nur vom Anbieter/Leanofy togglebar).

export type FeatureKey =
  | 'calendar'
  | 'appointments'
  | 'reminders'
  | 'recalls'
  | 'patients'
  | 'intake'
  | 'invoices'
  | 'reports'
  | 'communications'
  | 'inbox'
  | 'event-log'
  | 'gcal-sync'
  | 'smartai';

export interface FeatureDef {
  key: FeatureKey;
  /** i18n-Key der Bezeichnung (Namespace `nav` bzw. `settings.features.*`). */
  i18nKey: string;
  /** Optionaler i18n-Key für eine erläuternde Zeile unter dem Schalter. */
  hintKey?: string;
}

export const FEATURES: FeatureDef[] = [
  { key: 'calendar', i18nKey: 'nav.calendar' },
  { key: 'appointments', i18nKey: 'nav.appointments' },
  { key: 'reminders', i18nKey: 'nav.reminders', hintKey: 'settings.features.hintReminders' },
  { key: 'recalls', i18nKey: 'nav.recalls', hintKey: 'settings.features.hintRecalls' },
  { key: 'patients', i18nKey: 'nav.patients' },
  { key: 'intake', i18nKey: 'nav.intake' },
  { key: 'invoices', i18nKey: 'nav.invoices' },
  { key: 'reports', i18nKey: 'nav.reports' },
  { key: 'communications', i18nKey: 'settings.features.communications', hintKey: 'settings.features.hintCommunications' },
  { key: 'inbox', i18nKey: 'nav.inbox', hintKey: 'settings.features.hintInbox' },
  { key: 'event-log', i18nKey: 'nav.eventLog' },
  { key: 'gcal-sync', i18nKey: 'settings.tabGcal', hintKey: 'settings.features.hintGcal' },
  { key: 'smartai', i18nKey: 'settings.features.smartai', hintKey: 'settings.features.hintSmartai' },
];

export type FeatureMap = Record<FeatureKey, boolean>;

/** Default: alles an — bis /api/features geladen ist, wird nichts ausgeblendet. */
export const ALL_FEATURES_ON: FeatureMap = FEATURES.reduce(
  (acc, f) => ({ ...acc, [f.key]: true }),
  {} as FeatureMap
);

// --- Paket-Presets (Leanofy-Preismodell 39/69/99) ---------------------------
// Ein Preset setzt ALLE Flags auf einmal: enthaltene Module an, Rest aus.
// Paket 1 ist die reine Website — im Admin bleiben nur Dashboard/Hilfe.

export type PackageKey = 'website' | 'booking' | 'platform';

export interface PackageDef {
  key: PackageKey;
  /** i18n-Key des Paketnamens (Namespace settings.features.packages). */
  i18nKey: string;
  /** i18n-Key der Preiszeile/Beschreibung. */
  hintKey: string;
  /** Module, die dieses Paket enthält — alle anderen Flags werden ausgeschaltet. */
  features: FeatureKey[];
}

export const PACKAGES: PackageDef[] = [
  {
    key: 'website',
    i18nKey: 'settings.features.packages.website',
    hintKey: 'settings.features.packages.websiteHint',
    features: [],
  },
  {
    key: 'booking',
    i18nKey: 'settings.features.packages.booking',
    hintKey: 'settings.features.packages.bookingHint',
    // Basis-Patientenverwaltung gehört zu Paket 2: Buchung legt Akten
    // automatisch an, und Terminverwaltung ohne Patientenliste (Kontaktdaten,
    // Zuordnung) ist praktisch unbrauchbar. Paket 3 differenziert über
    // Anamnese, Belege, Recalls, CRM, Auswertungen, Audit-Log.
    features: ['appointments', 'calendar', 'gcal-sync', 'reminders', 'patients'],
  },
  {
    key: 'platform',
    i18nKey: 'settings.features.packages.platform',
    hintKey: 'settings.features.packages.platformHint',
    features: FEATURES.map((f) => f.key),
  },
];

/** Flag-Map eines Pakets: enthaltene Module an, alle übrigen aus. */
export function packageFeatureMap(pkg: PackageDef): FeatureMap {
  const on = new Set(pkg.features);
  return FEATURES.reduce(
    (acc, f) => ({ ...acc, [f.key]: on.has(f.key) }),
    {} as FeatureMap
  );
}

/** Erkennt, ob die aktuelle Map exakt einem Paket entspricht (für die Anzeige). */
export function matchPackage(map: FeatureMap): PackageKey | null {
  for (const pkg of PACKAGES) {
    const target = packageFeatureMap(pkg);
    if (FEATURES.every((f) => map[f.key] === target[f.key])) return pkg.key;
  }
  return null;
}
